import { makeResettableSignal, scheduleIdleTask } from "./schedulingUtils";

// Runs jobs sequentially, only when environment is idle. Higher priority jobs run first.
// Jobs with idleTimeout will run after timeout even if environment isn't idle (to prevent starvation).

type BackgroundJob<T> = {
  run: () => T;
  priority?: number;
  idleTimeout?: number; // Max time to wait for idle before running anyway
};

type BackgroundJobInternal = BackgroundJob<unknown> & {
  id: string;
  idleTimeoutId?: ReturnType<typeof setTimeout>;
};

export const startBackgroundJobRunner = ({
  waitUntilEnvironmentIdle = () => Promise.resolve(undefined),
}: {
  waitUntilEnvironmentIdle?: () => Promise<void>;
} = {}): {
  enqueue: <T>(job: BackgroundJob<T>) => Promise<T>;
  flush: () => Promise<void>;
  waitUntilIdle: () => Promise<void>;
} => {
  let jobs: BackgroundJobInternal[] = [];
  const jobsRanFromTimeout: Set<string> = new Set(); // Track jobs that ran via timeout to avoid double-execution
  const jobrunnerIdleSignal = makeResettableSignal();
  let isFlushing = false;

  const getNextJob = () => {
    // Remove jobs that already ran from timeout, then sort by priority
    jobs = jobs
      .filter((job) => !jobsRanFromTimeout.has(job.id))
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
    jobsRanFromTimeout.clear();
    return jobs.shift();
  };

  const processJobs = async () => {
    jobrunnerIdleSignal.reset(); // Signal that runner is busy
    while (jobs.length > 0 && !isFlushing) {
      await waitUntilEnvironmentIdle();
      const job = getNextJob();
      if (job) {
        await scheduleIdleTask(job.run); // Yield to browser before running
        // await job.run();
      }
    }
    jobrunnerIdleSignal.trigger(); // Signal that runner is idle
  };

  const enqueue = async <T>({
    run,
    idleTimeout,
    priority,
  }: BackgroundJob<T>): Promise<T> => {
    if (isFlushing) {
      await jobrunnerIdleSignal.wait(); // Wait for flush to complete
    }

    return new Promise(async (resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const id = crypto.randomUUID();
      const runWithResolve = () =>
        Promise.resolve(run()).then(resolve).catch(reject);

      // If idleTimeout set, run after timeout even if not idle (prevents starvation)
      if (idleTimeout) {
        timeoutId = setTimeout(async () => {
          jobsRanFromTimeout.add(id); // Mark as ran so processJobs skips it
          runWithResolve();
        }, idleTimeout);
      }

      jobs.push({
        id,
        run: runWithResolve,
        idleTimeout,
        priority,
        idleTimeoutId: timeoutId,
      });

      if (jobs.length === 1) {
        processJobs(); // Start processing loop if this is the first job
      }
    });
  };

  // Run all pending jobs immediately, bypassing idle checks
  const flush = async () => {
    for (const job of jobs) {
      if (job.idleTimeoutId) {
        clearTimeout(job.idleTimeoutId);
      }
    }
    isFlushing = true;
    await jobrunnerIdleSignal.wait(); // Wait for current job to finish
    jobrunnerIdleSignal.reset();
    const currentJobs = jobs
      .slice()
      .filter((job) => !jobsRanFromTimeout.has(job.id));
    jobs.length = 0;
    jobsRanFromTimeout.clear();
    for (const job of currentJobs) {
      await job.run();
    }
    isFlushing = false;
    jobrunnerIdleSignal.trigger();
  };

  const waitUntilIdle = () => jobrunnerIdleSignal.wait();

  return {
    enqueue,
    flush,
    waitUntilIdle,
  };
};
